<template>
  <div v-if="locationName === '' || locationName == null || false">
    <div class="row">
      <div class="col-12">
        <h5>Dieser Kunde hat keinen Standort.</h5>
        <p>Sie müssen den Standort auswählen, wenn Sie Maßnahmen hinzufügen oder ändern möchten</p>
      </div>
    </div>
  </div>
  <div v-else>
    <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
      <div class="row">
        <div class="col-12">
          <h6 class="mb-4">Standort: {{ locationName }}</h6>

          <AuditInfo
              :user-role="currentUserRole"
              :created-by-user-data="formData.createdByUserData"
              :created-at="formData.createdAt"
              :updated-by-user-data="formData.updatedByUserData"
              :updated-at="formData.updatedAt"/>

          <div v-if="
              formData.unemployedId === '' ||
              formData.unemployedId == null ||
               false">
            <h6 class="mb-4" style="color: red">{{ $t('general.error') }}</h6>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-6 col-md-12 col-sm-12">

          <SelectVue v-model="formData.designation"
                     :options="socioTherapyTypeDropdown"
                     :required="true"
                     :is-clear="false"
                     label="Bezeichnung"
                     :validation="vSocioTherapyCoaching$.designation"
                     :messages="vSocioTherapyCoaching$.designation.required.$message"
                     @update:modelValue="onChangeDesignation"/>

          <SelectVue v-model="formData.operationType"
                     :options="operationTypeDropdown"
                     :required="true"
                     label="Maßnahmenart"
                     :validation="vSocioTherapyCoaching$.operationType"
                     :messages="vSocioTherapyCoaching$.operationType.required.$message"/>

          <SelectVue v-model="formData.socioTherapistId"
                     :options="socioTherapistDropdown"
                     :required="true"
                     label="Coach"
                     placeholder="Wählen Sie Trainer"
                     :validation="vSocioTherapyCoaching$.socioTherapistId"
                     :messages="vSocioTherapyCoaching$.socioTherapistId.required.$message"/>

          <Datepicker
              v-model="formData.startDate"
              :required="true"
              label="Start"
              position="down"
              :validation="vSocioTherapyCoaching$.startDate"
              :messages="vSocioTherapyCoaching$.startDate.required.$message"/>

          <div class="row">
            <div class="col-6">
              <BaseNumber v-model="formData.duration"
                          type="number"
                          label="Dauer in Wochen"
                          :required=true
                          :validation="vSocioTherapyCoaching$.duration"
                          :messages="vSocioTherapyCoaching$.duration.required.$message"/>
            </div>
            <div class="col-6">
              <BaseNumber v-model="formData.units"
                          type="number"
                          label="Einheiten"
                          :required=true
                          :validation="vSocioTherapyCoaching$.units"
                          :messages="vSocioTherapyCoaching$.units.required.$message"/>
            </div>
          </div>


          <SelectVue v-model="formData.prescriberId"
                     :options="doctorDropdown"
                     :required="true"
                     label="Verordner"
                     placeholder="Auswählen Ärztin"
                     :validation="vSocioTherapyCoaching$.prescriberId"
                     :messages="vSocioTherapyCoaching$.prescriberId.required.$message"/>
          <div class="text-center">
            <BaseToggle
                v-model="formData.requiresSignature"
                label="Unterschriftspflichtig"/>
          </div>
        </div>

        <div class="col-lg-6 col-md-12 col-sm-12">
          <SelectVue v-model="formData.clientId"
                     :options="clientDropdown"
                     :required="true"
                     label="Kostenträger"
                     placeholder="Wählen Sie Kostenträger"
                     :validation="vSocioTherapyCoaching$.clientId"
                     :disabled="isClientIdDisabled"
                     :messages="vSocioTherapyCoaching$.clientId?.required?.$message"/>

          <SelectVue v-model="formData.billingType"
                     :options="billingTypeDropdown"
                     :required="true"
                     label="Art der Abrechnung"
                     :validation="vSocioTherapyCoaching$.billingType"
                     :messages="vSocioTherapyCoaching$.billingType.required.$message"/>

          <BaseInput v-model="formData.clerk"
                     label="Sachbearbeiter"/>

          <Datepicker
              v-model="formData.endDate"
              :required="true"
              label="Ende"
              position="down"
              :validation="vSocioTherapyCoaching$.endDate"
              :messages="vSocioTherapyCoaching$.endDate.required.$message"/>

          <BaseNumber v-model="formData.cost"
                      type="number"
                      label="Umsatz pro Einheit"
                      :required=true
                      :isCurrency="true"
                      :decimalPlaces="4"
                      :validation="vSocioTherapyCoaching$.cost"
                      :messages="vSocioTherapyCoaching$.cost.required.$message"/>

          <Datepicker
              v-model="formData.orderDate"
              :required="true"
              label="Verordnungsdatum"
              position="up"
              :validation="vSocioTherapyCoaching$.orderDate"
              :messages="vSocioTherapyCoaching$.orderDate.required.$message"/>

          <div class="text-center">
            <BaseToggle
                v-model="formData.digitalSignature"
                label="Digitale Unterschrift"/>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center w-100 mt-3">
        <SaveButton
            :label="$t('general.button.save')"
            :loading="isSubmitting"/>
      </div>
    </form>
  </div>
</template>

<script setup>
import socioTherapyCoachingService from '@/api/Unemployed/SocioTherapyCoaching';
import AuditInfo from '@/components/AuditInfo.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import {
  getAddressBookDoctorDropdown,
  getClientByLocationDropdown,
  getSocioTherapistByLocationDropdown,
} from '@/shared/dropdowns';
import { requiredRule } from '@/shared/validators/validationRules';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import useVuelidate from '@vuelidate/core';
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SaveButton from '@/components/button/SaveButton.vue';

const { t }                    = useI18n();
const piniaEnumsState          = piniaEnumsStore();
const getEnums                 = computed(() => piniaEnumsState.enums);
const piniaUserState           = piniaUserStore();
const currentUserRole          = computed(() => piniaUserState.currentUser?.user?.roles?.[0]);
const clientDropdown           = ref([]);
const billingTypeDropdown      = ref([]);
const operationTypeDropdown    = ref([]);
const socioTherapyTypeDropdown = ref([]);
const socioTherapistDropdown   = ref([]);
const doctorDropdown           = ref([]);

const isLoaded           = ref(false);
const isSubmitting       = ref(false);
const isClientIdDisabled = ref(false);
const emit               = defineEmits([ 'update:modelValue', 'update:submitForm' ]);

const props = defineProps({
  unemployedId: {
    type: Number,
    required: true,
  },
  locationName: {
    type: String,
    required: true,
  },
  locationId: {
    type: Number,
    required: true,
  },
  socioTherapyCoachingId: {
    type: Number,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const formData = ref({
  unemployedId: props.unemployedId, // !!! IMPORTANT
  clientId: '',
  designation: 'soziotherapie_privat',
  operationType: 'sociotherapie',
  billingType: 'individuell',
  clerk: '',
  startDate: '',
  endDate: '',
  duration: '',
  socioTherapistId: '',
  units: '',
  cost: '',
  prescriberId: '',
  orderDate: '',
  requiresSignature: true,
  digitalSignature: true,
});

const formRules = ref({
  unemployedId: requiredRule(t),
  clientId: requiredRule(t),
  designation: requiredRule(t),
  operationType: requiredRule(t),
  billingType: requiredRule(t),
  startDate: requiredRule(t),
  endDate: requiredRule(t),
  duration: requiredRule(t),
  socioTherapistId: requiredRule(t),
  units: requiredRule(t),
  cost: requiredRule(t),
  prescriberId: requiredRule(t),
  orderDate: requiredRule(t),
});

const computedRules          = computed(() => formRules.value);
const vSocioTherapyCoaching$ = useVuelidate(computedRules, formData);

async function submitForm() {
  vSocioTherapyCoaching$.value.$touch();
  if (vSocioTherapyCoaching$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (props.socioTherapyCoachingId) {
      await socioTherapyCoachingService.putSocioTherapyCoaching(
          formData.value, +props.socioTherapyCoachingId,
      );
    } else {
      await socioTherapyCoachingService.postSocioTherapyCoaching(formData.value);
    }

    SuccessToast(t('general.toasts.save_success'));
    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function applySocioTherapyCoachingData(data) {
  try {
    const designation      = data?.data?.designation;
    const clientId         = data?.data?.client?.id;
    const socioTherapistId = data?.data?.coach.id;
    const prescriberId     = data?.data?.prescriber?.id;

    // Add coach/client to dropdown if is logically deleted on backend and is selected for coaching
    addCoachToDropdown(data.data?.coach, socioTherapistDropdown);
    addClientToDropdown(data.data?.client, clientDropdown);

    formData.value = {
      ...data.data,
      unemployedId: props.unemployedId,
      clientId: clientId,
      socioTherapistId: socioTherapistId,
      prescriberId: prescriberId,
    };

    await onChangeDesignation(designation);
  } catch (error) {
    await ErrorToast(error);
  }
}

async function onChangeDesignation(designation) {
  if (designation === 'soziotherapie_privat') {
    isClientIdDisabled.value = true;
    formData.value.clientId  = null;
    formRules.value          = {
      ...formRules.value,
      clientId: '',
    };
  } else {
    isClientIdDisabled.value = false;
    formRules.value          = {
      ...formRules.value,
      clientId: requiredRule(t),
    };
  }
}

function addClientToDropdown(clientData, dropdown) {
  if (clientData?.id) {
    const client = {
      label: clientData.name,
      value: clientData.id,
    };
    const exists = dropdown.value.some(item => item.value === client.value);
    if (!exists) {
      dropdown.value.push(client);
    }
  }
}

function addCoachToDropdown(coachData, dropdown) {
  if (coachData?.id) {
    const coach  = {
      label: `${coachData.firstName} ${coachData.lastName}`,
      value: coachData.id,
    };
    const exists = dropdown.value.some(item => item.value === coach.value);
    if (!exists) {
      dropdown.value.push(coach);
    }
  }
}

async function getSocioTherapyEnumDropdowns() {
  billingTypeDropdown.value      = getEnums?.value?.socioTherapyBillingTypes || [];
  operationTypeDropdown.value    = getEnums?.value?.socioTherapyOperationTypes || [];
  socioTherapyTypeDropdown.value = getEnums?.value?.socioTherapyTypes || [];
}

onMounted(async () => {
  CloseToast();

  // Kick off in parallel with the dropdown loads below - it doesn't depend on them.
  const coachingDataPromise = props.socioTherapyCoachingId
      ? socioTherapyCoachingService.getSocioTherapyCoaching(props.socioTherapyCoachingId)
      : null;

  await Promise.all([
    getSocioTherapyEnumDropdowns(),
    getClientByLocationDropdown(clientDropdown, props.locationId),
    getSocioTherapistByLocationDropdown(socioTherapistDropdown, props.locationId),
    getAddressBookDoctorDropdown(doctorDropdown),
  ]);

  await onChangeDesignation(formData.value.designation);

  if (coachingDataPromise) {
    try {
      const {data} = await coachingDataPromise;
      await applySocioTherapyCoachingData(data);
    } catch (error) {
      await ErrorToast(error);
    }
  }

  isLoaded.value = true;
});


</script>
<style scoped>

</style>